import React, { useMemo } from 'react';

// 날짜 포맷 유틸리티 함수 (PC 버전 dashboardUtils 로직 보존)
const getLocalDateString = (dateObj = new Date()) => {
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * 모바일 홈 — AS 이력은 MobileMain 이 받아 내려준 것을 그대로 쓴다. (2026-09-23)
 *
 * 예전에는 이 화면이 asService.getRecords() 로 AS 이력을 '한 번 더' 받았다.
 * MobileMain 도 같은 자료를 받고 있었으니 앱을 열 때마다 같은 것을 두 번 내려받은 셈이다.
 * 그 때문에 MobileMain 쪽에 500건 제한을 걸어 두었는데, 정작 이 화면이 전량을 받고 있어
 * 데이터는 데이터대로 다 받으면서 캘린더·분석만 500건에서 잘려 나갔다.
 */
export default function MobileDashboard({ asList = [], loading = false }) {
  const { sales, weeklyReservations } = useMemo(() => {
    const safeAllAs = asList || [];

    const today = getLocalDateString();
    const thisMonth = today.substring(0, 7);
    const thisYear = today.substring(0, 4);

    // --- [1. 매출 — 완료 건만, 공급가액 기준] ---
    let dSales = 0, mSales = 0, ySales = 0;
    safeAllAs.filter(r => r.status === '완료').forEach(r => {
      const costWithVat = (Number(r.totalCost) || 0) + (Number(r.discount) || 0); // 할인 전 총액 기준으로 공급가 보정
      const costExVat = Math.round(costWithVat / 1.1); // 부가세 제외 공급가액 환산
      const date = r.settleDate || r.asDate;

      if (date === today) dSales += costExVat;
      if (date?.startsWith(thisMonth)) mSales += costExVat;
      if (date?.startsWith(thisYear)) ySales += costExVat;
    });

    // --- [2. 주간 AS 예약 및 대기 현황 (오늘 ~ 7일)] ---
    const nextWeekDate = new Date();
    nextWeekDate.setDate(nextWeekDate.getDate() + 6); // 오늘 포함 일주일 범위 지정
    const nextWeekStr = getLocalDateString(nextWeekDate);

    // '예약' 뿐 아니라 '접수' 상태도 주간 스케줄러에 함께 보여준다.
    const reservations = safeAllAs.filter(r =>
      (r.status === '예약' || r.status === '접수') && r.asDate >= today && r.asDate <= nextWeekStr
    ).sort((a, b) => a.asDate.localeCompare(b.asDate));

    return {
      sales: { daily: dSales, monthly: mSales, yearly: ySales },
      weeklyReservations: reservations
    };
  }, [asList]);

  return (
    <div style={{ padding: '16px', boxSizing: 'border-box', backgroundColor: '#f8fafc' }}>
      
      {loading && (
        <div style={{ textAlign: 'center', color: '#64748b', padding: '20px', fontSize: '14px' }}>
          🔄 실시간 수치 동기화 중...
        </div>
      )}

      {/* 🚀 1. 경영 매출 3줄 수직 요약 섹션 */}
      {!loading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
          {/* 일일 매출 */}
          <div style={{ background: '#ffffff', padding: '16px 20px', borderRadius: '16px', borderLeft: '6px solid #3b82f6', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.03)', borderTop: '1px solid #e2e8f0', borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0' }}>
            <div style={{ color: '#64748b', fontSize: '12px', fontWeight: 'bold' }}>오늘 매출 (공급가액)</div>
            <div style={{ fontSize: '22px', fontWeight: '900', color: '#1e293b', marginTop: '6px' }}>₩ {sales.daily.toLocaleString()}</div>
          </div>
          {/* 월간 누적 매출 */}
          <div style={{ background: '#ffffff', padding: '16px 20px', borderRadius: '16px', borderLeft: '6px solid #10b981', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.03)', borderTop: '1px solid #e2e8f0', borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0' }}>
            <div style={{ color: '#64748b', fontSize: '12px', fontWeight: 'bold' }}>이달 누적 매출 (공급가액)</div>
            <div style={{ fontSize: '22px', fontWeight: '900', color: '#1e293b', marginTop: '6px' }}>₩ {sales.monthly.toLocaleString()}</div>
          </div>
          {/* 연간 매출 */}
          <div style={{ background: '#ffffff', padding: '16px 20px', borderRadius: '16px', borderLeft: '6px solid #f59e0b', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.03)', borderTop: '1px solid #e2e8f0', borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0' }}>
            <div style={{ color: '#64748b', fontSize: '12px', fontWeight: 'bold' }}>연간 매출 총계 (공급가액)</div>
            <div style={{ fontSize: '22px', fontWeight: '900', color: '#1e293b', marginTop: '6px' }}>₩ {sales.yearly.toLocaleString()}</div>
          </div>
        </div>
      )}

      {/* 🚀 2. 주간 AS 예약 및 대기 현황 섹션 */}
      {!loading && (
        <div style={{ background: '#ffffff', padding: '20px', borderRadius: '18px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.04)', border: '1px solid #e2e8f0' }}>
          <div style={{ fontSize: '14px', fontWeight: '800', color: '#1e293b', marginBottom: '16px', borderBottom: '2px solid #f1f5f9', paddingBottom: '10px' }}>
            🗓️ 주간 AS 예약 및 대기 현황 (7일 이내)
          </div>

          {weeklyReservations.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {weeklyReservations.map((r) => (
                <div key={r.id} style={{ background: '#f8fafc', padding: '14px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      <span style={{ fontSize: '12px', color: '#9333ea', fontWeight: 'bold', background: '#f3e8ff', padding: '3px 8px', borderRadius: '6px' }}>
                        {r.asDate}
                      </span>
                      {/* 🚀 [신규 적용] 모바일 현장 맞춤형 예약/접수 구분 상태 뱃지 라벨 */}
                      <span style={{ 
                        fontSize: '10px', 
                        padding: '2px 6px', 
                        borderRadius: '4px', 
                        fontWeight: 'bold',
                        background: r.status === '접수' ? '#e0f2fe' : '#f3e8ff',
                        color: r.status === '접수' ? '#0369a1' : '#6b21a8'
                      }}>
                        {r.status}
                      </span>
                    </div>
                    <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '600' }}>
                      담당: {r.mechanic || '미지정'}
                    </span>
                  </div>
                  <div style={{ fontSize: '14px', fontWeight: '800', color: '#1e293b', marginBottom: '4px' }}>
                    {r.customerName} <span style={{ color: '#94a3b8', fontWeight: 'normal', fontSize: '13px' }}>{r.carNumber && `(${r.carNumber})`}</span>
                  </div>
                  <div style={{ fontSize: '12px', color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    💬 {r.symptom || '일반 정검 스케줄'}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', color: '#94a3b8', padding: '30px 10px', fontSize: '13px' }}>
              일주일 이내에 잡혀있는 예약 및 대기 일정이 없습니다.
            </div>
          )}
        </div>
      )}

    </div>
  );
}